import { Typography } from "@/components/common/Typography/Typography"
import { AddTagButton } from "@/components/feature/KnowledgebaseFileListItem/AddTagButton"
import {
  DocumentTagBadge,
  type DocumentTagEntry,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { TagActionMenu } from "@/components/feature/KnowledgebaseFilters/TagActionMenu"
import { Button } from "@/components/ui/button"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { DEFAULT_CATEGORY_COLOR } from "@/constants/KnowledgeBaseConstant"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import {
  IconFile,
  IconFileTypeCsv,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypePdf,
  IconFileTypeTxt,
  IconFileTypeXls,
} from "@tabler/icons-react"
import { Check, Pencil, Plus } from "lucide-react"
import * as React from "react"

export type TablerIcon = typeof IconFile

const FILE_TYPE_ICON_MAP: Record<string, TablerIcon> = {
  pdf: IconFileTypePdf,
  doc: IconFileTypeDoc,
  docx: IconFileTypeDocx,
  xls: IconFileTypeXls,
  xlsx: IconFileTypeXls,
  csv: IconFileTypeCsv,
  txt: IconFileTypeTxt,
}

export const getFileTypeIcon = (fileName: string): TablerIcon => {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? ""
  return FILE_TYPE_ICON_MAP[ext] ?? IconFile
}

import { getLocalizedLabel } from "@/utils/localizedLabel"

export { getLocalizedLabel }

export interface CategoryTagEditorProps {
  category: KnowledgeBaseCategory
  selectedEntries: DocumentTagEntry[]
  selectedTagIds: string[]
  isCreatingTag: boolean
  onEditCategory: (category: KnowledgeBaseCategory) => void
  onSelectionChange: (values: string[]) => void
  onRemoveTag?: (entry: DocumentTagEntry) => void
  onCreateTag: (
    category: KnowledgeBaseCategory,
    tagName: string,
  ) => Promise<string | undefined>
  onRenameTag?: (
    tagId: string,
    categoryId: string,
    newName: string,
  ) => Promise<void>
  onDeleteTag?: (tagId: string, categoryId: string) => Promise<void>
}

export const CategoryTagEditor: React.FC<CategoryTagEditorProps> = ({
  category,
  selectedEntries,
  selectedTagIds,
  isCreatingTag,
  onEditCategory,
  onSelectionChange,
  onRemoveTag,
  onCreateTag,
  onRenameTag,
  onDeleteTag,
}) => {
  const { currentLanguage, translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")

  const trimmedQuery = query.trim()
  const normalizedQuery = trimmedQuery.toLocaleLowerCase()

  const categoryLabel = React.useMemo(
    () =>
      getLocalizedLabel(
        category.displayNameEn,
        category.displayNameDe,
        currentLanguage,
      ),
    [category.displayNameDe, category.displayNameEn, currentLanguage],
  )

  const selectedTagIdSet = React.useMemo(
    () => new Set(selectedTagIds),
    [selectedTagIds],
  )

  const tagOptions = React.useMemo(
    () =>
      category.tags
        .filter((tag) => tag.isActive)
        .filter((tag) => {
          if (!normalizedQuery) return true
          const label = getLocalizedLabel(
            tag.displayNameEn,
            tag.displayNameDe,
            currentLanguage,
          ).toLocaleLowerCase()
          const value = tag.id.toLocaleLowerCase()
          return (
            label.includes(normalizedQuery) || value.includes(normalizedQuery)
          )
        }),
    [category.tags, currentLanguage, normalizedQuery],
  )

  const handleToggle = (tagId: string) => {
    const nextSelected = new Set(selectedTagIdSet)
    if (nextSelected.has(tagId)) {
      nextSelected.delete(tagId)
    } else {
      nextSelected.add(tagId)
    }
    onSelectionChange(Array.from(nextSelected))
  }

  const handleCreate = async () => {
    if (!trimmedQuery) return
    const createdTagId = await onCreateTag(category, trimmedQuery)
    if (createdTagId) {
      handleToggle(createdTagId)
    }
    setQuery("")
    setOpen(false)
  }

  return (
    <div className="group/category">
      <div className="flex items-center gap-1.5">
        <div
          className="h-4 w-1 rounded-sm"
          style={{ backgroundColor: category.color || DEFAULT_CATEGORY_COLOR }}
        />
        <Typography variant="textS">{categoryLabel}</Typography>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 opacity-0 transition-opacity group-focus-within/category:opacity-100 group-hover/category:opacity-100"
          onClick={() => onEditCategory(category)}
          aria-label={translate("KNOWLEDGEBASE/CATEGORY_EDIT_TRIGGER")}
          title={translate("KNOWLEDGEBASE/CATEGORY_EDIT_TRIGGER")}
        >
          <Pencil />
        </Button>
      </div>

      {selectedEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {selectedEntries.map((entry) => (
            <DocumentTagBadge
              key={entry.tag.id}
              entry={entry}
              onRemove={() => onRemoveTag?.(entry)}
            />
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <AddTagButton onClick={() => setOpen(true)} />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[300px] p-0" disablePortal>
          <div className="border-b border-grey4 p-3">
            <div className="flex items-center gap-2">
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    void handleCreate()
                  }
                }}
                placeholder={translate("KNOWLEDGEBASE/TAG_ADD_PLACEHOLDER")}
                className="h-9 text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => void handleCreate()}
                disabled={trimmedQuery.length === 0 || isCreatingTag}
              >
                <Plus className="size-4" />
              </Button>
            </div>
          </div>

          <Command shouldFilter={false} className="bg-grey1 text-grey6">
            <CommandList className="max-h-[220px]">
              {tagOptions.length === 0 && trimmedQuery.length === 0 && (
                <CommandEmpty>
                  {translate("KNOWLEDGEBASE/TAG_NO_RESULTS")}
                </CommandEmpty>
              )}
              {tagOptions.length === 0 && trimmedQuery.length > 0 && (
                <CommandGroup>
                  <CommandItem
                    onSelect={() => void handleCreate()}
                    disabled={isCreatingTag}
                  >
                    <Plus className="size-4" />
                    <span className="truncate">
                      {translate("KNOWLEDGEBASE/TAG_CREATE_ACTION", {
                        tag: trimmedQuery,
                      })}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {tagOptions.length > 0 && (
                <CommandGroup>
                  {tagOptions.map((tag) => {
                    const isSelected = selectedTagIdSet.has(tag.id)
                    return (
                      <CommandItem
                        key={tag.id}
                        onSelect={() => handleToggle(tag.id)}
                        className="justify-between text-sm"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div
                            aria-hidden="true"
                            className={classNames(
                              "grid h-4 w-4 shrink-0 place-content-center rounded-sm border border-primary shadow-sm",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "",
                            )}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                          <span className="truncate">
                            {getLocalizedLabel(
                              tag.displayNameEn,
                              tag.displayNameDe,
                              currentLanguage,
                            )}
                          </span>
                        </div>
                        {(onRenameTag || onDeleteTag) && (
                          <TagActionMenu
                            label={getLocalizedLabel(
                              tag.displayNameEn,
                              tag.displayNameDe,
                              currentLanguage,
                            )}
                            onRename={
                              onRenameTag
                                ? (value) =>
                                    onRenameTag(tag.id, tag.categoryId, value)
                                : undefined
                            }
                            onDelete={
                              onDeleteTag
                                ? () => onDeleteTag(tag.id, tag.categoryId)
                                : undefined
                            }
                          />
                        )}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
              {selectedTagIds.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => onSelectionChange([])}
                      className="justify-center text-center"
                    >
                      {translate("KNOWLEDGEBASE/BULK_TAG_CLEAR_ADDED")}
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
